import type { IsThisInclude, PaymentCycle, PaymentMethod, PaymentStatus } from '@libs/enums';
import type { ItemAttributes, ItemNames } from '@ps-smart-cp/ppfw-client';
import {
  Bool,
  Characters,
  DateRange,
  type DateRangeObject,
  ItemName,
  Locale,
  Numeric,
  type OrEmpty,
  SingleDate,
  SingleValue,
  StringRange,
  type StringRangeObject,
  ValueSet,
} from '@ps-smart-cp/ppfw-client';

/**
 * リース支払情報
 */
interface Schema {
  /**
   * リース料総額
   */
  totalLeaseFee: string;
  /**
   * 消費税総額
   */
  consumptionTaxOverAllAmount: string;
  /**
   * 解約後リース料
   */
  laFeeAfterCancel: string;
  /**
   * 解約後リース料(税込)
   */
  laFeeAfterCancelInTax: string;
  /**
   * 解約後最終回リース料
   */
  laLastFeeAfterCancel: string;
  /**
   * 解約後最終回リース料(税込)
   */
  laLastFeeInTaxAfterCancel: string;
  /**
   * 控除額1
   */
  deductionAmountOne: string;
  /**
   * 控除額2
   */
  deductionAmountTwo: string;
  /**
   * 控除額3
   */
  deductionAmountThree: string;
  /**
   * 控除額4
   */
  deductionAmountFour: string;
  /**
   * 支払済
   */
  paidClass: PaymentStatus;
  /**
   * 消費税率
   */
  consumptionTaxRate: string;
  /**
   * 支払リース料
   */
  laPaymentFee: string;
  /**
   * 支払リース料
   */
  leasePaymentFee: string;
  /**
   * 支払リース料(税込)
   */
  laPaymentFeeInTax: string;
  /**
   * 支払年月日
   */
  paymentDate: Date | null;
  /**
   * 支払年月
   */
  paymentYearMonth: DateRangeObject;
  /**
   * 支払年月日(テキスト)
   */
  rawPaymentDate: string;
  /**
   * 支払日
   */
  paymentDay: string;
  /**
   * リース料予定計算用支払サイクル
   */
  leaseFeePaymentCycle: OrEmpty<PaymentCycle>;
  /**
   * 支払方法
   */
  paymentMethod: PaymentMethod;
  /**
   * 支払方法(テキスト)
   */
  rawPaymentMethod: string;
  /**
   * 支払開始年月日
   */
  paymentStartDate: Date | null;
  /**
   * 支払開始年月日(テキスト)
   */
  rawPaymentStartDate: string;
  /**
   * 2回目支払年月
   */
  paymentYmSecond: Date | null;
  /**
   * 2回目支払年月(テキスト)
   */
  rawPaymentYmSecond: string;
  /**
   * 消費税総額(契約)
   */
  contractConsumptionTaxOverAllAmount: string;
  /**
   * 支払サイクル
   */
  paymentCycle: PaymentCycle;
  /**
   * 支払サイクル(テキスト)
   */
  rawPaymentCycle: string;
  /**
   * 支払回数
   */
  paymentCount: string;
  /**
   * 支払回数(抽出条件)
   */
  paymentCountCondition: StringRangeObject;
  /**
   * 初回リース料
   */
  laFirstFee: string;
  /**
   * 初回リース料(税込)
   */
  laFirstFeeInTax: string;
  /**
   * 最終回リース料
   */
  laLastFee: string;
  /**
   * 最終回リース料(税込)
   */
  laLastFeeInTax: string;
  /**
   * 前回支払年月日
   */
  previousPaymentDate: Date | null;
  /**
   * 前回支払年月日(テキスト)
   */
  rawPreviousPaymentDate: string;
  /**
   * 支払満了(抽出条件)
   */
  paymentExpiration: IsThisInclude;
  /**
   * 支払方法(抽出条件)
   */
  paymentMethodCheckGroupCondition: Set<PaymentMethod>;
  /**
   * 支払開始年月日(抽出条件)
   */
  paymentStartDateCondition: DateRangeObject;
  /**
   * 支払満了年月日(抽出条件)
   */
  paymentExpirationDateCondition: DateRangeObject;
  /**
   * 支払満了年月日
   */
  paymentExpirationDate: Date | null;
  /**
   * 未経過リース料
   */
  unexpiredLeaseFees: string;
  /**
   * 未経過リース料(確定)
   */
  unexpiredLeaseFeesClosed: string;
  /**
   * 未経過消費税額
   */
  unexpiredConsumptionTax: string;
  /**
   * 未経過消費税額(確定)
   */
  unexpiredConsumptionTaxClosed: string;
  /**
   * 支払満了(Grid表示用)
   */
  paymentExpirationGrid: PaymentStatus;
  /**
   * 消費税額
   */
  consumptionTax: string;
  /**
   * 支払リース料(控除前)
   */
  leasePaymentFeeBeforeDeduction: string;
  /**
   * 支払リース料(税込)
   */
  leasePaymentFeeInTax: string;
  /**
   * 初回リース料
   */
  leaseFirstFee: string;
  /**
   * 初回リース料(税込)
   */
  leaseFirstFeeInTax: string;
  /**
   * 最終回リース料
   */
  leaseLastFee: string;
  /**
   * 最終回リース料(税込)
   */
  leaseLastFeeInTax: string;
  /**
   * 控除額1
   */
  deductionAmount01: string;
  /**
   * 控除額2
   */
  deductionAmount02: string;
  /**
   * 控除額3
   */
  deductionAmount03: string;
  /**
   * 控除額4
   */
  deductionAmount04: string;
  /**
   * 契約上支払フラグ
   */
  isContractualPayment: boolean;
}

const attributes = {
  totalLeaseFee: Numeric.preset.currency(),
  consumptionTaxOverAllAmount: Numeric.preset.currency(),
  laFeeAfterCancel: Numeric.preset.currency(),
  laFeeAfterCancelInTax: Numeric.preset.currency(),
  laLastFeeAfterCancel: Numeric.preset.currency(),
  laLastFeeInTaxAfterCancel: Numeric.preset.currency(),
  deductionAmountOne: Numeric.preset.currency(),
  deductionAmountTwo: Numeric.preset.currency(),
  deductionAmountThree: Numeric.preset.currency(),
  deductionAmountFour: Numeric.preset.currency(),
  paidClass: SingleValue.shortValue(),
  consumptionTaxRate: Numeric.of(0, 1, 3),
  laPaymentFee: Numeric.preset.currency(),
  leasePaymentFee: Numeric.preset.currency(),
  laPaymentFeeInTax: Numeric.preset.currency(),
  paymentDate: SingleDate.ymd(),
  paymentYearMonth: DateRange.ym(),
  paymentDay: Numeric.of(1, 31, 0),
  leaseFeePaymentCycle: SingleValue.shortValue(),
  paymentMethod: SingleValue.longValue(),
  paymentStartDate: SingleDate.ymd(),
  paymentYmSecond: SingleDate.ym(),
  paymentCycle: SingleValue.shortValue(),
  paymentCount: Numeric.of(1, 999, 0),
  paymentCountCondition: StringRange.of(
    Characters.preset.numerical({ maxLen: 4 }),
    Characters.preset.numerical({ maxLen: 4 }),
  ),
  laFirstFee: Numeric.preset.currency(),
  laFirstFeeInTax: Numeric.preset.currency(),
  laLastFee: Numeric.preset.currency(),
  laLastFeeInTax: Numeric.preset.currency(),
  previousPaymentDate: SingleDate.ymd(),
  contractConsumptionTaxOverAllAmount: Numeric.preset.currency(),
  leasePaymentFeeBeforeDeduction: Numeric.preset.currency(),
  paymentExpiration: SingleValue.shortValue(),
  paymentMethodCheckGroupCondition: ValueSet.new(),
  paymentStartDateCondition: DateRange.ymd(),
  paymentExpirationDateCondition: DateRange.ymd(),
  paymentExpirationDate: SingleDate.ymd(),
  unexpiredLeaseFees: Numeric.preset.currency(),
  unexpiredLeaseFeesClosed: Numeric.preset.currency(),
  unexpiredConsumptionTax: Numeric.preset.currency(),
  unexpiredConsumptionTaxClosed: Numeric.preset.currency(),
  paymentExpirationGrid: SingleValue.longValue(),
  consumptionTax: Numeric.preset.currency(),
  leasePaymentFeeInTax: Numeric.preset.currency(),
  leaseFirstFee: Numeric.preset.currency(),
  leaseFirstFeeInTax: Numeric.preset.currency(),
  leaseLastFee: Numeric.preset.currency(),
  leaseLastFeeInTax: Numeric.preset.currency(),
  deductionAmount01: Numeric.preset.currency(),
  deductionAmount02: Numeric.preset.currency(),
  deductionAmount03: Numeric.preset.currency(),
  deductionAmount04: Numeric.preset.currency(),
  isContractualPayment: Bool.new(),
} satisfies ItemAttributes<Schema>;

interface Record {
  totalLeaseFee: string;
  consumptionTaxOverAllAmount: string;
  laFeeAfterCancel: string;
  laFeeAfterCancelInTax: string;
  laLastFeeAfterCancel: string;
  laLastFeeInTaxAfterCancel: string;
  deductionAmountOne?: string;
  deductionAmountTwo?: string;
  deductionAmountThree?: string;
  deductionAmountFour?: string;
  paidClass: string;
  isPaid: string;
  consumptionTaxRate: string;
  laPaymentFee: string;
  leasePaymentFee: string;
  laPaymentFeeInTax: string;
  paymentDate: string;
  paymentYearMonthFrom: string;
  paymentYearMonthTo: string;
  paymentDay: string;
  leaseFeePaymentCycle: string;
  paymentMethod: string;
  paymentStartDate: string;
  paymentYmSecond: string;
  contractConsumptionTaxOverAllAmount: string;
  paymentCycle: string;
  paymentCount: string;
  paymentCountConditionFrom: string;
  paymentCountConditionTo: string;
  laFirstFee: string;
  laFirstFeeInTax: string;
  laLastFee: string;
  laLastFeeInTax: string;
  previousPaymentDate: string;
  paymentExpiration: string;
  paymentMethodList: string[];
  paymentExpirationDateFrom: string;
  paymentExpirationDateTo: string;
  paymentExpirationDate: string;
  paymentStartDateFrom: string;
  paymentStartDateTo: string;
  unexpiredLeaseFees: string;
  unexpiredLeaseFeesClosed: string;
  unexpiredConsumptionTax: string;
  unexpiredConsumptionTaxClosed: string;
  paymentExpirationGrid: string;
  consumptionTax: string;
  leasePaymentFeeBeforeDeduction: string;
  leasePaymentFeeInTax: string;
  leaseFirstFee: string;
  leaseFirstFeeInTax: string;
  leaseLastFee: string;
  leaseLastFeeInTax: string;
  deductionAmount01?: string;
  deductionAmount02?: string;
  deductionAmount03?: string;
  deductionAmount04?: string;
  isContractualPayment: boolean;
}

const names = {
  totalLeaseFee: ItemName.of([Locale.ja, 'リース料総額'], [Locale.en, 'Total Lease Fee']),
  consumptionTaxOverAllAmount: ItemName.of(
    [Locale.ja, '消費税総額'],
    [Locale.en, 'Consumption Tax Over All Amount'],
  ),
  laFeeAfterCancel: ItemName.of([Locale.ja, '解約後リース料'], [Locale.en, 'La Fee After Cancel']),
  laFeeAfterCancelInTax: ItemName.of(
    [Locale.ja, '解約後リース料(税込)'],
    [Locale.en, 'La Fee After Cancel In Tax'],
  ),
  laLastFeeAfterCancel: ItemName.of(
    [Locale.ja, '解約後最終回リース料'],
    [Locale.en, 'La Last Fee After Cancel'],
  ),
  laLastFeeInTaxAfterCancel: ItemName.of(
    [Locale.ja, '解約後最終回リース料(税込)'],
    [Locale.en, 'La Last Fee In Tax After Cancel'],
  ),
  deductionAmountOne: ItemName.of([Locale.ja, '控除額1'], [Locale.en, 'Deduction Amount One']),
  deductionAmountTwo: ItemName.of([Locale.ja, '控除額2'], [Locale.en, 'Deduction Amount Two']),
  deductionAmountThree: ItemName.of([Locale.ja, '控除額3'], [Locale.en, 'Deduction Amount Three']),
  deductionAmountFour: ItemName.of([Locale.ja, '控除額4'], [Locale.en, 'Deduction Amount Four']),
  paidClass: ItemName.of([Locale.ja, '支払済']),
  consumptionTaxRate: ItemName.of([Locale.ja, '消費税率'], [Locale.en, 'Vat Rate']),
  laPaymentFee: ItemName.of([Locale.ja, '支払リース料'], [Locale.en, 'La Payment Fee']),
  leasePaymentFee: ItemName.of([Locale.ja, '支払リース料']),
  laPaymentFeeInTax: ItemName.of([Locale.ja, '支払リース料(税込)'], [Locale.en, 'La Payment Fee In Tax']),
  paymentDate: ItemName.of([Locale.ja, '支払年月日'], [Locale.en, 'Payment Date']),
  paymentYearMonth: ItemName.of([Locale.ja, '支払年月'], [Locale.en, 'Payment Year Month']),
  paymentDay: ItemName.of([Locale.ja, '支払日'], [Locale.en, 'Payment Day']),
  leaseFeePaymentCycle: ItemName.of(
    [Locale.ja, 'リース料予定計算用支払サイクル'],
    [Locale.en, 'Payment Cycle for Lease Fee Schedule Calculation'],
  ),
  paymentMethod: ItemName.of([Locale.ja, '支払方法'], [Locale.en, 'Payment Method']),
  paymentStartDate: ItemName.of([Locale.ja, '支払開始年月日']),
  paymentYmSecond: ItemName.of([Locale.ja, '2回目支払年月']),
  contractConsumptionTaxOverAllAmount: ItemName.of([Locale.ja, '消費税総額(契約)']),
  paymentCycle: ItemName.of([Locale.ja, '支払サイクル']),
  paymentCount: ItemName.of([Locale.ja, '支払回数']),
  laFirstFee: ItemName.of([Locale.ja, '初回リース料']),
  laFirstFeeInTax: ItemName.of([Locale.ja, '初回リース料(税込)']),
  laLastFee: ItemName.of([Locale.ja, '最終回リース料']),
  laLastFeeInTax: ItemName.of([Locale.ja, '最終回リース料(税込)']),
  previousPaymentDate: ItemName.of([Locale.ja, '前回支払年月日']),
  leasePaymentFeeBeforeDeduction: ItemName.of([Locale.ja, '支払リース料(控除前)']),
  paymentExpiration: ItemName.of([Locale.ja, '支払満了']),
  paymentMethodCheckGroupCondition: ItemName.of([Locale.ja, '支払方法']),
  paymentStartDateCondition: ItemName.of([Locale.ja, '支払開始年月日']),
  paymentExpirationDateCondition: ItemName.of([Locale.ja, '支払満了年月日']),
  paymentExpirationDate: ItemName.of([Locale.ja, '支払満了年月日']),
  unexpiredLeaseFees: ItemName.of([Locale.ja, '未経過リース料']),
  unexpiredLeaseFeesClosed: ItemName.of([Locale.ja, '未経過リース料(確定)']),
  unexpiredConsumptionTax: ItemName.of([Locale.ja, '未経過消費税額']),
  unexpiredConsumptionTaxClosed: ItemName.of([Locale.ja, '未経過消費税額(確定)']),
  paymentExpirationGrid: ItemName.of([Locale.ja, '支払満了']),
  consumptionTax: ItemName.of([Locale.ja, '消費税額']),
  leasePaymentFeeInTax: ItemName.of([Locale.ja, '支払リース料(税込)']),
  leaseFirstFee: ItemName.of([Locale.ja, '初回リース料']),
  leaseFirstFeeInTax: ItemName.of([Locale.ja, '初回リース料(税込)']),
  leaseLastFee: ItemName.of([Locale.ja, '最終回リース料']),
  leaseLastFeeInTax: ItemName.of([Locale.ja, '最終回リース料(税込)']),
  deductionAmount01: ItemName.of([Locale.ja, '控除額1']),
  deductionAmount02: ItemName.of([Locale.ja, '控除額2']),
  deductionAmount03: ItemName.of([Locale.ja, '控除額3']),
  deductionAmount04: ItemName.of([Locale.ja, '控除額4']),
  isContractualPayment: ItemName.of([Locale.ja, '契約上支払フラグ']),
} satisfies ItemNames<Schema>;

const labelNames = {
  paymentInformation: ItemName.of([Locale.ja, '支払情報']),
  leaseContractTotalLeaseFee: ItemName.of([Locale.ja, 'リース料総額(リース契約)']),
  leaseContractConsumptionTaxOverAllAmount: ItemName.of([Locale.ja, '消費税総額(リース契約)']),
};

const laPayment = {
  names,
  labelNames,
  attributes,
};

interface LaPayment {
  schema: Schema;
  record: Record;
}

export { laPayment };
export type { LaPayment };
